import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Hidden,
  IconButton,
  ImageList,
  ImageListItem,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';

import { getRequest } from 'utils/api';
import { closeModal, openModal } from 'features/utilsSlice';
import { setSelectedNetworkIdsFilter, getSelectedNetworkIdsFilter } from 'utils/localStorage';
import { setDiscoverGender, setDiscoverPopularityConditions, setDiscoverCountry } from 'features/discoverFiltersSlice';
import { ONLYFANS_NETWORK_ID, countryToFlag } from 'utils/helpers';
import massPayCountries from 'utils/massPayCountries';
import SearchIcon from 'public/icons/search-main.svg';
import FilterIcon from 'public/icons/filter.svg';
import useCurrentUser from 'hooks/useCurrentUser';
import useCurrentProfile from 'hooks/useCurrentProfile';
import SuccessGigDialog from './SuccessGigDialog';
import FeaturedMatches from './Matches/FeaturedMatches';
import NetworkIcon from 'components/NetworkIcon';
import AnimateList from 'components/animations/AnimateList';
import AnimateListItem from 'components/animations/AnimateListItem';
import InfluencerProfileCardList from 'components/InfluencerProfileCardList';
import DropdownMenu from 'components/DropdownMenu';
import FilterDialog from 'components/FilterDialog';
import SimpleCard from 'components/SimpleCard';
import IOSSwitch from 'components/IOSSwitch';
import SimpleSortDropdown from 'components/SimpleSortDropdown';
import styles from 'styles/components/Discover.module.scss';

const ALL_COUNTRIES = 'All Countries';

const countryFlagMap = new Map(massPayCountries.map(c => [c.name, countryToFlag(c.code)]));
const getCountryFlag = name => countryFlagMap.get(name) || '';

const size = { minWidth: 65, height: 75 };

const listStyle = {
  flexWrap: 'nowrap',
  scrollSnapType: 'x mandatory',
  overflowY: 'hidden',
  position: 'relative',
};

const minWidth = { minWidth: 160 };

const menuItems = [
  { id: 0, name: 'Trending', icon: '' },
  { id: 1, name: 'Relevance', icon: '' },
  { id: 2, name: 'Alphabetical', icon: '' },
  { id: 3, name: 'Newest', icon: '' },
  { id: 4, name: 'Popular', icon: '' },
  { id: 5, name: 'Oldest', icon: '' },
];

const discoverGenderDefault = gender => {
  if (!gender) return 'all';

  const g = gender.split(' ')[1];
  return ['male', 'female'].includes(g) ? g : 'all';
};

const DiscoverInfluencers = ({ fitContent = {} }) => {
  const [selectedNetworkIds, setSelectedNetworkIds] = useState([]);
  const [usedBeforeProfiles, setUsedBeforeProfiles] = useState([]);
  const [guaranteedGains, setGuaranteedGains] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [hovered, setHovered] = useState(null);
  const [isLoading, setIsLoading] = useState({ profiles: false, usedProfiles: false });
  const [domReady, setDomReady] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [countries, setCountries] = useState([]);

  const dispatch = useDispatch();
  const theme = useTheme();
  const containerRef = useRef();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const currentUser = useCurrentUser();
  const currentProfile = useCurrentProfile();
  const filter = useSelector(state => state.discoverFilters.filter);
  const popularityConditions = useSelector(state => state.discoverFilters.popularityConditions);
  const rating = useSelector(state => state.discoverFilters.rating);
  const gender = useSelector(state => state.discoverFilters.gender);
  const traits = useSelector(state => state.discoverFilters.traitIds);
  const sort = useSelector(state => state.discoverFilters.sort);
  const country = useSelector(state => state.discoverFilters.country);
  const networks = useSelector(state => state.utils.networks);

  const getSelectedNetworks = () =>
    networks.filter(network => selectedNetworkIds.includes(network.id));

  const selectNetwork = id => {
    if (selectedNetworkIds.includes(id) && id in popularityConditions) {
      const { [id]: _, ...rest } = popularityConditions;
      dispatch(setDiscoverPopularityConditions(rest));
    }

    setSelectedNetworkIds(prevIds =>
      prevIds.includes(id) ? prevIds.filter(i => i !== id) : [...prevIds, id]
    );

    if (selectedNetworkIds.includes(id)) setHovered(null);
  };

  const debouncedHandleChange = debounce(e => setInputValue(e.target.value), 1000);

  // Add when added to the backend
  // const sortedUsedBeforeProfiles = usedBeforeProfiles.sort(
  //   (a, b) => new Date(b.created_at) - new Date(a.created_at)
  // );

  const loadMore = () => {
    setIsLoading(prevState => ({ ...prevState, profiles: true }));

    const currentPage = page + 1;

    getRequest({
      endpoint: 'profiles/discover',
      query: {
        q: inputValue,
        network_ids: selectedNetworkIds,
        popularity_conditions: popularityConditions,
        influencer_networks: true,
        guaranteed_gains: guaranteedGains,
        per_page: 25,
        page: currentPage,
        min_rating: rating,
        gender,
        filter,
        sort,
        trait_ids: traits,
        country,
      },
      resolveResponse: true,
    })
      .then(response => {
        const data = response.status === 204 ? [] : response.data.profiles;
        const count = response.headers['pagination-total-count'] || 0;

        setHasMore(+count > profiles.length + data.length);
        setProfiles(prevData => [...prevData, ...data]);
        setIsLoading(prevState => ({ ...prevState, profiles: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, profiles: false })))
      .finally(() => setPage(currentPage));
  };

  useEffect(() => {
    const isolateTouch = e => e.stopPropagation();

    const containerNode = containerRef.current;

    if (!containerNode && networks.length === 0) return;

    containerNode.addEventListener('touchstart', isolateTouch, { passive: true });
    containerNode.addEventListener('touchmove', isolateTouch, { passive: true });
    containerNode.addEventListener('touchend', isolateTouch, { passive: true });

    return () => {
      if (!containerNode && networks.length === 0) return;

      containerNode.removeEventListener('touchstart', isolateTouch, { passive: true });
      containerNode.removeEventListener('touchmove', isolateTouch, { passive: true });
      containerNode.removeEventListener('touchend', isolateTouch, { passive: true });
    };
  }, [networks]);

  useEffect(() => {
    const selectedNetworkIdsFilter = getSelectedNetworkIdsFilter();

    const noSelectedNetworkIdsFilter =
      selectedNetworkIdsFilter?.length === 0 || !selectedNetworkIdsFilter;

    // If an adult and no filters are set, default to OnlyFans
    if (noSelectedNetworkIdsFilter && currentProfile?.adult) setSelectedNetworkIds([6]);

    if (noSelectedNetworkIdsFilter) return;

    // OnlyFans and an adult profile
    if (selectedNetworkIdsFilter[0] === ONLYFANS_NETWORK_ID && !currentProfile?.adult) return;
    if (selectedNetworkIdsFilter) setSelectedNetworkIds(selectedNetworkIdsFilter);

    if (currentProfile?.adult) {
      // dispatch(setDiscoverGender(discoverGenderDefault(currentProfile?.gender))); Default to profile gender
      dispatch(setDiscoverGender(discoverGenderDefault(null)));
    }
  }, [currentProfile?.adult, currentProfile?.gender, dispatch]);

  useEffect(() => {
    setSelectedNetworkIdsFilter(JSON.stringify(selectedNetworkIds));
  }, [selectedNetworkIds, dispatch, sort]);

  useEffect(() => {
    if (selectedNetworkIds.length === 0 && inputValue.length === 0) {
      setPage(1);
      setProfiles([]);
      setHasMore(false);
      return;
    }

    setIsLoading(prevState => ({ ...prevState, profiles: true }));
    setPage(1);

    getRequest({
      endpoint: 'profiles/discover',
      query: {
        q: inputValue,
        network_ids: selectedNetworkIds,
        popularity_conditions: popularityConditions,
        influencer_networks: true,
        guaranteed_gains: guaranteedGains,
        min_rating: rating,
        per_page: 25,
        sort,
        page: 1,
        filter,
        gender,
        trait_ids: traits,
        country,
      },
      resolveResponse: true,
    })
      .then(response => {
        const data = response.status === 204 ? [] : response.data.profiles;
        const count = response.headers['pagination-total-count'] || 0;

        setHasMore(+count > data.length);
        setProfiles(data);
        setIsLoading(prevState => ({ ...prevState, profiles: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, profiles: false })));
  }, [
    selectedNetworkIds,
    filter,
    guaranteedGains,
    inputValue,
    rating,
    popularityConditions,
    gender,
    sort,
    traits,
    country,
  ]);

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, usedProfiles: true }));

    getRequest({
      endpoint: 'profiles/discover',

      query: {
        filter: 'used',
        network_ids: selectedNetworkIds,
        influencer_networks: true,
      },
    })
      .then(response => {
        setUsedBeforeProfiles(response.profiles);
        setIsLoading(prevState => ({ ...prevState, usedProfiles: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, usedProfiles: false })));

    return () => dispatch(closeModal('Full Profile'));
  }, [selectedNetworkIds, dispatch]);

  useEffect(() => setDomReady(true), []);

  useEffect(() => {
    getRequest({ endpoint: 'profiles/countries' })
      .then(data => setCountries(data || []))
      .catch(() => setCountries([]));
  }, []);

  const priorityOrder = ['OnlyFans', 'Twitter'];

  const filteredNetworks =
    currentProfile?.adult || currentUser?.adult
      ? networks
          .filter(network => network.name !== 'Image & Likeness')
          .sort((a, b) => {
            const indexA = priorityOrder.indexOf(a.name);
            const indexB = priorityOrder.indexOf(b.name);

            if (indexA === -1 && indexB === -1) return 0; // Neither are in priority list
            if (indexA === -1) return 1; // `b` has priority, move `a` down
            if (indexB === -1) return -1; // `a` has priority, move `b` down

            return indexA - indexB; // Sort based on predefined order
          })
      : networks.filter(
          network => !['Image & Likeness', 'OnlyFans', 'LoyalFans'].includes(network.name)
        );

  return (
    <>
      <FeaturedMatches />

      {!isLoading.usedProfiles && usedBeforeProfiles.length > 0 && (
        <motion.div
          className={styles.purpleBox}
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
        >
          <h4>ORDER AGAIN?</h4>

          <div className={styles.cards}>
            {usedBeforeProfiles.map(profile => (
              <SimpleCard profile={profile} key={profile.id} />
            ))}
          </div>
        </motion.div>
      )}

      <div
        style={isDesktop ? { padding: '20px 40px 0' } : { padding: '20px 15px 80px' }}
        className={styles.overflow}
      >
        <div className={styles.filters}>
          <TextField
            type="text"
            variant="outlined"
            placeholder="Search by exact name"
            onChange={debouncedHandleChange}
            className={clsx({ [fitContent]: !isEmpty(fitContent) }, styles.input)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <AnimateList key={networks[0]?.id} className={styles.networks}>
            <ImageList style={listStyle} ref={containerRef}>
              {filteredNetworks.map(network => (
                <AnimateListItem key={network.id} style={size}>
                  <ImageListItem>
                    <div
                      onMouseEnter={() => setHovered(network.id)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => selectNetwork(network.id)}
                    >
                      <Hidden smDown>
                        <NetworkIcon
                          active={selectedNetworkIds.includes(network.id)}
                          network={network}
                          hovered={hovered === network.id}
                          hasName={false}
                          styles={styles}
                        />
                      </Hidden>

                      <Hidden mdUp>
                        <NetworkIcon
                          active={selectedNetworkIds.includes(network.id)}
                          network={network}
                          hasName={false}
                          styles={styles}
                        />
                      </Hidden>
                    </div>
                  </ImageListItem>
                </AnimateListItem>
              ))}
            </ImageList>
          </AnimateList>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/*  OnlyFans and an adult profile  */}
            {selectedNetworkIds.includes(ONLYFANS_NETWORK_ID) && (
              <>
                <div style={{ fontSize: 16 }}>Guaranteed Gains:</div>
                <IOSSwitch
                  checked={guaranteedGains}
                  onChange={e => setGuaranteedGains(e.target.checked)}
                  name="guaranteed_gains"
                />
              </>
            )}
          </div>

          {countries.length > 0 && (
            <SimpleSortDropdown
              menuItems={[
                { name: ALL_COUNTRIES },
                ...countries.map(c => ({ name: c, icon: getCountryFlag(c) })),
              ]}
              selectedItem={
                !country || country === 'all'
                  ? ALL_COUNTRIES
                  : `${getCountryFlag(country)} ${country}`.trim()
              }
              setSelectedItem={name =>
                dispatch(setDiscoverCountry(name === ALL_COUNTRIES ? 'all' : name))
              }
              maxHeight={300}
            />
          )}

          <DropdownMenu
            menuItems={menuItems}
            divider={false}
            menuStyle={minWidth}
            labelPrefix="Sort by "
            right
          />
        </div>

        <InfluencerProfileCardList
          data={profiles}
          setData={setProfiles}
          isLoading={isLoading.profiles}
          inputValue={inputValue}
          hasMore={hasMore}
          loadMore={loadMore}
          selectedNetworkIds={selectedNetworkIds}
        />

        {domReady && !isLoading.profiles && (
          <>
            {createPortal(
              <div
                className={clsx(styles.filterBtnContainer, {
                  [styles.moveBtn]:
                    (!currentUser.agency || !currentUser.full_management_agency) &&
                    currentUser.primary_interest === 'buy',
                })}
              >
                <IconButton onClick={() => dispatch(openModal({ name: 'Filter Influencers' }))}>
                  <FilterIcon />
                </IconButton>
              </div>,

              document.getElementById('filter-button-portal')
            )}

            <FilterDialog selectedNetworks={getSelectedNetworks} />

            <SuccessGigDialog
              title="Awesome"
              subtitle="You invited the influencer to this gig!"
              description="Influencer will have up to 48 hours to accept or reject this gig invite. You will be notified when they respond."
              btnText="Ok Cool"
            />
          </>
        )}
      </div>
    </>
  );
};

export default DiscoverInfluencers;
